import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FlameDetectionRange } from "@domain/product/flame-detection-range";
import {
  FLAME_DETECTOR_SENSITIVITIES,
  FlameDetectorSensitivity,
  getFlameDetectorSensitivityLabel,
} from "@domain/product/flame-detector-sensitivity";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanCustomPartNumbersComponent } from "@project/floorplanner/floorplan-custom-part-numbers/floorplan-custom-part-numbers.component";

export interface SensitivityOption {
  value: FlameDetectorSensitivity;
  label: string;
  available: boolean;
  detectionLength?: number;
}

@Component({
  selector: "app-floorplan-flame-detector-detail-form",
  imports: [UiKitModule, ReactiveFormsModule, FloorplanCustomPartNumbersComponent],
  templateUrl: "./floorplan-flame-detector-detail-form.component.html",
  styleUrl: "./floorplan-flame-detector-detail-form.component.scss",
})
export class FloorplanFlameDetectorDetailFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);

  readonly flameDetector = input.required<FloorplanFlameDetector | FloorplanFlameDetectorPlaceholder>();

  protected readonly selectedTabIndex = signal<number>(0);
  protected readonly sensitivityOptions = signal<SensitivityOption[]>([]);
  protected readonly noOptionsAvailable = computed(() => this.sensitivityOptions().every((o) => !o.available));
  protected editFlameDetectorForm!: FormGroup;

  protected readonly noSensitivtyOptionsAvailablePlaceholder: string = $localize`:@@editFlameDetectorForm.sensitivity.noOptionsAvailable.placeholder:Keine Empfindlichkeit auswählbar`;

  ngOnInit() {
    this.initSensitivityOptions();
    this.initForm();
  }

  private initForm() {
    const initialSensitivity = this.flameDetector().sensitivity ?? this.getDefaultSensitivity();
    this.editFlameDetectorForm = this.formBuilder.group({
      sensitivity: [initialSensitivity],
      mountingHeight: [this.flameDetector().mountingHeight],
    });

    if (!this.flameDetector().sensitivity && initialSensitivity) {
      this.flameDetector().sensitivity = initialSensitivity;
      this.updateCoverageAreaFromSensitivity(initialSensitivity as FlameDetectorSensitivity);
    }

    this.editFlameDetectorForm.valueChanges.subscribe(() => {
      if (!this.editFlameDetectorForm.valid || this.editFlameDetectorForm.pristine) return;
      this.update();
    });
  }

  private initSensitivityOptions() {
    const flameDetector = this.flameDetector();

    if (!(flameDetector instanceof FloorplanFlameDetector)) {
      this.sensitivityOptions.set(this.buildDefaultSensitivityOptions());
      return;
    }

    const detectionRange = flameDetector.config.detectionRange;
    this.sensitivityOptions.set(this.buildSensitivityOptionsFromDetectionRange(detectionRange));
  }

  private buildSensitivityOptionsFromDetectionRange(detectionRange: FlameDetectionRange): SensitivityOption[] {
    const detectionLengthByLevel: Record<FlameDetectorSensitivity, number | undefined | null> = {
      [FlameDetectorSensitivity.HIGH]: detectionRange.detectionRangeHigh,
      [FlameDetectorSensitivity.STANDARD]: detectionRange.detectionRangeDefault,
      [FlameDetectorSensitivity.LOW]: detectionRange.detectionRangeLow,
    };

    return FLAME_DETECTOR_SENSITIVITIES.map((sensitivity) => {
      const detectionLength = detectionLengthByLevel[sensitivity];
      return {
        value: sensitivity,
        label: getFlameDetectorSensitivityLabel(sensitivity),
        available: detectionLength !== undefined && detectionLength !== null && detectionLength > 0,
        detectionLength: detectionLength ?? undefined,
      };
    });
  }

  private buildDefaultSensitivityOptions(): SensitivityOption[] {
    return FLAME_DETECTOR_SENSITIVITIES.map((sensitivity) => ({
      value: sensitivity,
      label: getFlameDetectorSensitivityLabel(sensitivity),
      available: true,
    }));
  }

  private update() {
    const newSensitivity = this.editFlameDetectorForm.value.sensitivity;
    const previousSensitivity = this.flameDetector().sensitivity;

    this.flameDetector().sensitivity = newSensitivity;
    this.flameDetector().mountingHeight = this.editFlameDetectorForm.value.mountingHeight;

    if (newSensitivity !== previousSensitivity) {
      this.updateCoverageAreaFromSensitivity(newSensitivity);
    }
  }

  private updateCoverageAreaFromSensitivity(sensitivity: FlameDetectorSensitivity) {
    const option = this.sensitivityOptions().find((o) => o.value === sensitivity);
    if (!option?.detectionLength) {
      return;
    }

    const flameDetector = this.flameDetector();
    if (flameDetector instanceof FloorplanFlameDetector) {
      flameDetector.updateCoverageAreaLength(option.detectionLength);
    } else if (flameDetector instanceof FloorplanFlameDetectorPlaceholder) {
      flameDetector.updateCoverageAreaLength(option.detectionLength);
    }
  }

  private getDefaultSensitivity(): string | undefined {
    const options = this.sensitivityOptions();
    const standardOption = options.find((o) => o.value === FlameDetectorSensitivity.STANDARD && o.available);
    if (standardOption) {
      return standardOption.value;
    }
    return options.find((o) => o.available)?.value;
  }
}
